import { useState } from "react";

import { Button, PricingBillingPeriodPicker, PricingPage } from "@shapr3d/frontend-lib";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { type TierPriceConfig, usePriceConfig } from "../../hooks/usePriceConfig";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { SubscriptionPeriod, SubscriptionTier } from "../../types";
import { formatCurrency } from "../../utils/currencyUtils";
import { getWebAppPricingConfigs } from "./pricingConfigs";

import style from "./PricingCardsSection.module.css";

export const PricingCardsSection: React.FC = () => {
  const { data: priceConfig } = usePriceConfig();
  const { data: accountData } = useAccount();
  const navigation = useNavigation();
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const isTrial =
    accountData?.availablePlans?.some((plan) => plan.trialDays > 0) ||
    accountData?.techTrialAvailable;
  const configs = getWebAppPricingConfigs(t);
  const [period, setPeriod] = useState<SubscriptionPeriod>(SubscriptionPeriod.Yearly);

  const isYearly = period === SubscriptionPeriod.Yearly;

  function getPriceDisplay(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    return isYearly
      ? formatCurrency(Math.round((tierConfig.yearly / 12) * 100) / 100, priceConfig.currencyCode)
      : formatCurrency(tierConfig.monthly, priceConfig.currencyCode);
  }

  function getSubtitle(tierConfig: TierPriceConfig | undefined) {
    if (!priceConfig || !tierConfig) return undefined;
    const includesTax = tierConfig.priceIncludesTax;
    if (!isYearly) {
      return includesTax
        ? t(m.Purchase.PriceBilledMonthlyIncluding)
        : t(m.Purchase.PriceBilledMonthly);
    }
    const billedAnnually = includesTax
      ? t(m.Purchase.PriceBilledAnnuallyIncluding)
      : t(m.Purchase.PriceBilledAnnually);
    return t(m.Purchase.PriceBilledAnnuallyWithPrice, {
      price: formatCurrency(tierConfig.yearly, priceConfig.currencyCode),
      billedAnnually,
    });
  }

  const individualTier = priceConfig?.tiers[SubscriptionTier.Individual];
  const studioTier = priceConfig?.tiers[SubscriptionTier.Studio];

  const savingsText = individualTier
    ? t(m.Purchase.PriceMonthlySave, { savingsPercent: individualTier.savingsPercent })
    : undefined;

  function withCurrency(price: React.ReactNode) {
    if (typeof price !== "string") return price;
    return priceConfig ? price.replace("$", priceConfig.currencySymbol) : price;
  }

  return (
    <div className={style.section}>
      <PricingBillingPeriodPicker
        isYearly={isYearly}
        onYearlyChange={(isYearly) => {
          tracker.trackButtonClick({
            action: ElementClickAction.MONTHLY_YEARLY_TOGGLE,
            location: ElementClickLocation.PRICE_CARD_SECTION,
            details: isYearly ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly,
          });
          setPeriod(isYearly ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly);
        }}
        monthlyLabel={t(m.Button.Monthly)}
        yearlyLabel={t(m.Button.Yearly)}
        yearlyExtra={savingsText}
      />
      <PricingPage
        className={style.pricingPage}
        free={{
          ...configs.free,
          price: withCurrency(configs.free.price),
          seatTypesLabel: t(m.PricingCards.AvailableSeatTypes),
          ctaSlot: (
            <Button
              type="button"
              variant="grayscale"
              disabled
              aria-current="true"
              className={style.cardCta}
            >
              {t(m.PricingCards.Free.Tagline)}
            </Button>
          ),
        }}
        individual={{
          ...configs.individual,
          price: getPriceDisplay(individualTier),
          subtitle: getSubtitle(individualTier),
          seatTypesLabel: t(m.PricingCards.AvailableSeatTypes),
          ctaSlot: (
            <Button
              variant="primary"
              className={style.cardCta}
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.SUBSCRIBE_NOW_SOLO,
                  location: ElementClickLocation.PRICE_CARD_SECTION,
                });
                navigation.goToCheckout({ selectedTier: SubscriptionTier.Individual, period });
              }}
            >
              {isTrial ? t(m.PurchaseNotice.StartFreeTrial) : t(m.Checkout.SubscribeNow)}
            </Button>
          ),
        }}
        studio={{
          ...configs.studio,
          price: getPriceDisplay(studioTier),
          subtitle: getSubtitle(studioTier),
          seatTypesLabel: t(m.PricingCards.AvailableSeatTypes),
          ctaSlot: (
            <Button
              variant="grayscale"
              className={style.cardCta}
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.SUBSCRIBE_NOW_STUDIO,
                  location: ElementClickLocation.PRICE_CARD_SECTION,
                });
                navigation.goToCheckout({ selectedTier: SubscriptionTier.Studio, period });
              }}
            >
              {isTrial ? t(m.PurchaseNotice.StartFreeTrial) : t(m.Checkout.SubscribeNow)}
            </Button>
          ),
        }}
        enterprise={{
          ...configs.enterprise,
          price: "Contact Sales",
          pricePrefix: undefined,
          pricePeriod: "",
          priceSubline: undefined,
          subtitle: undefined,
          seatTypesLabel: t(m.PricingCards.AvailableSeatTypes),
          ctaSlot: (
            <Button
              variant="grayscale"
              className={style.cardCta}
              style={{ isolation: "isolate" }}
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.GO_TO_CONTACT_US,
                  location: ElementClickLocation.PRICE_CARD_SECTION,
                });
                window.open(
                  "https://www.shapr3d.com/contact-sales",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              {t(m.Button.ContactUs)}
            </Button>
          ),
        }}
      />
    </div>
  );
};
